/**
 * Inline Search Component
 *
 * Dropdown search panel that appears below the header
 * Replaces the old full-screen blurred modal
 */

import { useState, useEffect, useRef } from 'react';
import { useInfiniteSearch } from '@/hooks/useSearch';
import type { SearchResult as BaseSearchResult } from '@/services/search.service';
import type { SearchFilters } from '@/types/api.types';
import { useAppStore } from '@/store/app.store';
import { AdvancedFilters } from './AdvancedFilters';

interface ContextMessage {
  id: string;
  content: string;
  created_at: string;
  users: {
    display_name: string | null;
    first_name: string;
    last_name: string;
  };
}

interface SearchResult extends BaseSearchResult {
  contextBefore?: ContextMessage[];
  contextAfter?: ContextMessage[];
}

interface InlineSearchProps {
  onClose: () => void;
}

type SearchTab = 'all' | 'channels' | 'dms' | 'media' | 'links' | 'files';

export const InlineSearch = ({ onClose }: InlineSearchProps) => {
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<SearchTab>('all');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [expandedResults, setExpandedResults] = useState<Set<string>>(new Set());
  const [advancedFilters, setAdvancedFilters] = useState<Partial<SearchFilters>>({});
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { setActiveView, setSelectedChannelId, setSelectedDMThreadId } = useAppStore();

  const toggleResultContext = (resultId: string) => {
    setExpandedResults((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(resultId)) {
        newSet.delete(resultId);
      } else {
        newSet.add(resultId);
      }
      return newSet;
    });
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Fetch search results with infinite scroll
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteSearch(
    {
      query: debouncedQuery,
      type: activeTab === 'all' ? undefined : activeTab === 'channels' || activeTab === 'dms' ? undefined : activeTab,
      sortBy: 'relevance',
      limit: 50,
      ...advancedFilters,
    },
    {
      enabled: debouncedQuery.trim().length > 0,
    }
  );

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!observerTarget.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 }
    );
    const target = observerTarget.current;
    if (target) observer.observe(target);
    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const searchData = data
    ? {
        results: data.pages.flatMap((page) => page.results),
        total: data.pages[0]?.total || 0,
      }
    : { results: [], total: 0 };

  const groupedResults = {
    channels: searchData?.results.filter(r => r.channel_id) || [],
    dms: searchData?.results.filter(r => r.dm_thread_id) || [],
    media: searchData?.results.filter(r => r.message_attachments && r.message_attachments.length > 0) || [],
    links: searchData?.results.filter(r => r.message_links && r.message_links.length > 0) || [],
    files: searchData?.results.filter(r => r.message_attachments && r.message_attachments.length > 0) || [],
  };

  const counts = {
    channels: groupedResults.channels.length,
    dms: groupedResults.dms.length,
    media: groupedResults.media.length,
    links: groupedResults.links.length,
    files: groupedResults.files.length,
  };

  const handleResultClick = (result: SearchResult) => {
    if (result.channel_id) {
      setActiveView('channels');
      setSelectedChannelId(result.channel_id);
    } else if (result.dm_thread_id) {
      setActiveView('dms');
      setSelectedDMThreadId(result.dm_thread_id);
    }
    onClose();
  };

  const highlightMatch = (text: string, q: string) => {
    if (!q.trim()) return text;
    const parts = text.split(new RegExp(`(${q})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === q.toLowerCase() ?
        <mark key={i} className="bg-yellow-200 dark:bg-yellow-800/50 text-gray-900 dark:text-yellow-200">{part}</mark> :
        part
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('bg-BG', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const displayResults: SearchResult[] = activeTab === 'all' ? searchData?.results || [] :
    activeTab === 'channels' ? groupedResults.channels :
    activeTab === 'dms' ? groupedResults.dms :
    activeTab === 'media' ? groupedResults.media :
    activeTab === 'links' ? groupedResults.links :
    groupedResults.files;

  return (
    <div className="relative z-40">
      {/* Semi-transparent backdrop */}
      <div className="fixed inset-0 bg-black/10 dark:bg-black/30" style={{ top: '3.5rem' }} />

      {/* Search Panel */}
      <div
        ref={panelRef}
        className="relative bg-white dark:bg-dark-bg-elevated border-b border-gray-200 dark:border-dark-border-primary shadow-lg"
      >
        {/* Search Input */}
        <div className="p-3 border-b border-gray-200 dark:border-dark-border-primary">
          <div className="relative max-w-3xl mx-auto">
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-text-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Търсене в канали, съобщения, файлове..."
              className="w-full pl-10 pr-16 py-2.5 border border-gray-300 dark:border-dark-border-secondary bg-white dark:bg-dark-bg-input rounded-lg text-gray-900 dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-muted focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 text-sm"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 text-[10px] font-semibold text-gray-400 dark:text-dark-text-muted bg-gray-100 dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border-secondary rounded">
                ESC
              </kbd>
            </div>
          </div>
        </div>

        {/* Advanced Filters */}
        <AdvancedFilters
          filters={advancedFilters}
          onFiltersChange={setAdvancedFilters}
          isOpen={showAdvancedFilters}
          onToggle={() => setShowAdvancedFilters(!showAdvancedFilters)}
        />

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-dark-border-primary overflow-x-auto max-w-3xl mx-auto">
          {[
            { key: 'all' as SearchTab, label: 'Всички' },
            { key: 'channels' as SearchTab, label: 'Канали', count: counts.channels },
            { key: 'dms' as SearchTab, label: 'Съобщения', count: counts.dms },
            { key: 'media' as SearchTab, label: 'Медия', count: counts.media },
            { key: 'links' as SearchTab, label: 'Линкове', count: counts.links },
            { key: 'files' as SearchTab, label: 'Файлове', count: counts.files },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400'
                  : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-secondary'
              }`}
            >
              {tab.label} {tab.count && tab.count > 0 ? `(${tab.count})` : ''}
            </button>
          ))}
        </div>

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto">
          <div className="max-w-3xl mx-auto">
            {!debouncedQuery.trim() && (
              <div className="p-6 text-center text-gray-500 dark:text-dark-text-muted">
                <svg className="mx-auto h-10 w-10 text-gray-400 dark:text-dark-text-muted mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="text-sm">Започнете да пишете за търсене</p>
              </div>
            )}

            {isLoading && (
              <div className="p-6 text-center">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                <p className="mt-2 text-xs text-gray-500 dark:text-dark-text-muted">Търсене...</p>
              </div>
            )}

            {isError && (
              <div className="p-6 text-center text-red-600 dark:text-red-400">
                <p className="text-sm">Грешка при търсене</p>
                <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">{error?.message}</p>
              </div>
            )}

            {!isLoading && !isError && debouncedQuery.trim() && displayResults.length === 0 && (
              <div className="p-6 text-center text-gray-500 dark:text-dark-text-muted text-sm">
                Няма резултати за "{debouncedQuery}"
              </div>
            )}

            {!isLoading && !isError && displayResults.length > 0 && (
              <div className="divide-y divide-gray-200 dark:divide-dark-border-primary">
                {displayResults.map((result) => {
                  const showContext = expandedResults.has(result.id);
                  const hasContext = (result.contextBefore && result.contextBefore.length > 0) ||
                                     (result.contextAfter && result.contextAfter.length > 0);

                  return (
                    <div key={result.id}>
                      <div
                        onClick={() => handleResultClick(result)}
                        className="p-3 hover:bg-gray-50 dark:hover:bg-dark-bg-hover cursor-pointer transition-colors"
                      >
                        <div className="flex items-start justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">
                              {result.users.display_name || `${result.users.first_name} ${result.users.last_name}`}
                            </span>
                            {result.channel && (
                              <span className="text-xs text-gray-500 dark:text-dark-text-muted">
                                в #{result.channel.name}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-gray-400 dark:text-dark-text-muted">
                            {formatDate(result.created_at)}
                          </span>
                        </div>

                        {result.content && (
                          <p className="text-sm text-gray-900 dark:text-dark-text-primary bg-yellow-50 dark:bg-yellow-900/20 p-1.5 rounded text-[13px]">
                            {highlightMatch(result.content, debouncedQuery)}
                          </p>
                        )}

                        {result.message_attachments && result.message_attachments.length > 0 && (
                          <div className="mt-1.5 flex items-center space-x-1 text-xs text-gray-500 dark:text-dark-text-muted">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            <span>{result.message_attachments.length} файл(а)</span>
                          </div>
                        )}

                        {result.message_links && result.message_links.length > 0 && (
                          <div className="mt-1.5 flex items-center space-x-1 text-xs text-gray-500 dark:text-dark-text-muted">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                            <span>{result.message_links.length} линк(а)</span>
                          </div>
                        )}

                        {hasContext && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleResultContext(result.id);
                            }}
                            className="mt-1.5 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 font-medium flex items-center space-x-1"
                          >
                            <svg
                              className={`w-3.5 h-3.5 transition-transform ${showContext ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                            <span>{showContext ? 'Скрий контекста' : 'Покажи контекста'}</span>
                          </button>
                        )}
                      </div>

                      {showContext && hasContext && (
                        <div className="bg-gray-50 dark:bg-dark-bg-tertiary px-3 py-2 space-y-1.5 border-t border-gray-200 dark:border-dark-border-primary">
                          {result.contextBefore && result.contextBefore.length > 0 && (
                            <div className="space-y-1">
                              {result.contextBefore.map((msg) => (
                                <div key={msg.id} className="text-sm text-gray-600 dark:text-dark-text-secondary pl-3 border-l-2 border-gray-300 dark:border-dark-border-secondary">
                                  <span className="font-medium text-gray-700 dark:text-dark-text-primary">
                                    {msg.users.display_name || `${msg.users.first_name} ${msg.users.last_name}`}
                                  </span>
                                  <span className="text-xs text-gray-400 dark:text-dark-text-muted ml-2">
                                    {formatDate(msg.created_at)}
                                  </span>
                                  <p className="text-gray-600 dark:text-dark-text-secondary">{msg.content}</p>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="flex items-center space-x-2 py-0.5">
                            <div className="h-px flex-1 bg-yellow-300 dark:bg-yellow-700" />
                            <span className="text-[10px] font-semibold text-yellow-600 dark:text-yellow-400 uppercase">Резултат</span>
                            <div className="h-px flex-1 bg-yellow-300 dark:bg-yellow-700" />
                          </div>

                          {result.contextAfter && result.contextAfter.length > 0 && (
                            <div className="space-y-1">
                              {result.contextAfter.map((msg) => (
                                <div key={msg.id} className="text-sm text-gray-600 dark:text-dark-text-secondary pl-3 border-l-2 border-gray-300 dark:border-dark-border-secondary">
                                  <span className="font-medium text-gray-700 dark:text-dark-text-primary">
                                    {msg.users.display_name || `${msg.users.first_name} ${msg.users.last_name}`}
                                  </span>
                                  <span className="text-xs text-gray-400 dark:text-dark-text-muted ml-2">
                                    {formatDate(msg.created_at)}
                                  </span>
                                  <p className="text-gray-600 dark:text-dark-text-secondary">{msg.content}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                <div ref={observerTarget} className="p-3 text-center">
                  {isFetchingNextPage && (
                    <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
                  )}
                  {hasNextPage && !isFetchingNextPage && (
                    <button
                      onClick={() => fetchNextPage()}
                      className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 font-medium"
                    >
                      Зареди още резултати
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        {displayResults.length > 0 && (
          <div className="p-2 border-t border-gray-200 dark:border-dark-border-primary bg-gray-50 dark:bg-dark-bg-secondary text-center text-xs text-gray-500 dark:text-dark-text-muted">
            {displayResults.length} резултат(а)
          </div>
        )}
      </div>
    </div>
  );
};
